import React, { useState, useEffect, useMemo } from 'react';
import { Box, Collapse, Stack, Typography, TextField } from '@mui/material';

import { ConfigSwitchSection, ParentAwareSaveBar } from '../../common';
import Switch from '../../common/Switch';
import { useGetCallbacksQuery, useGetConfigsQuery } from '../../store/modules/merchant/api';
import { useMerchant } from '../../contexts/MerchantContext';
import { FulfillmentState, useFullfillmentSubmit } from '../../hooks/useFulfillmentSubmit';
import { merchantDomainErrorMessage as validateCallbackUrl } from '../../util/helpers';
import PageWrapper from '../../common/PageWrapper';
import type { CallbackType } from '../../store/modules/merchant/types';
import {
  getCallbackDisplayLabel,
  inheritableLabelsFromConfigNames,
  uniqueDisplayLabels,
} from '../../util/parentSettingSave';

function getFulfillmentInheritableSaveBarLabels(
  original: FulfillmentState,
  updated: FulfillmentState,
): string[] {
  const labels: string[] = [];

  if (original.isShippingEnabled !== updated.isShippingEnabled) {
    labels.push(...inheritableLabelsFromConfigNames(['NO_SHIPPING_REQUIRED']));
  }

  const callbackToggles: Array<{
    type: CallbackType;
    wasEnabled: boolean;
    isEnabled: boolean;
  }> = [
    {
      type: 'Products',
      wasEnabled: original.isOrderResolveEnabled,
      isEnabled: updated.isOrderResolveEnabled,
    },
    {
      type: 'ShippingAndTax',
      wasEnabled: original.isOrderChangeEnabled,
      isEnabled: updated.isOrderChangeEnabled,
    },
    {
      type: 'Fulfillability',
      wasEnabled: original.isOrderVerifyEnabled,
      isEnabled: updated.isOrderVerifyEnabled,
    },
  ];

  for (const { type, wasEnabled, isEnabled } of callbackToggles) {
    if (wasEnabled !== isEnabled) {
      labels.push(getCallbackDisplayLabel(type));
    }
  }

  if (original.callbackUrl !== updated.callbackUrl && updated.isOrderResolveEnabled) {
    labels.push(getCallbackDisplayLabel('Products'));
    if (updated.isOrderChangeEnabled) {
      labels.push(getCallbackDisplayLabel('ShippingAndTax'));
    }
    if (updated.isOrderVerifyEnabled) {
      labels.push(getCallbackDisplayLabel('Fulfillability'));
    }
  }

  return uniqueDisplayLabels(labels);
}

const FulfillmentComponent = () => {
  const { merchantId, isLoading: merchantInfoLoading, canUpdateMerchantSettings } = useMerchant();
  const { data: callbacks, isLoading: isLoadingCallbacks } = useGetCallbacksQuery(merchantId, {
    skip: !merchantId,
  });
  const { data: configs, isLoading: isLoadingConfigs } = useGetConfigsQuery(merchantId, {
    skip: !merchantId,
  });

  const noShippingConfig = configs?.find((config) => config.name === 'NO_SHIPPING_REQUIRED');
  const originalFulfillmentState: FulfillmentState = useMemo(
    () => ({
      isShippingEnabled: noShippingConfig?.value !== true,
      isOrderResolveEnabled: !!callbacks?.find((cb) => cb.callbackType === 'Products'),
      isOrderChangeEnabled: !!callbacks?.find((cb) => cb.callbackType === 'ShippingAndTax'),
      isOrderVerifyEnabled: !!callbacks?.find((cb) => cb.callbackType === 'Fulfillability'),
      callbackUrl: callbacks?.find((cb) => cb.callbackType === 'Products')?.url || '',
    }),
    [noShippingConfig, callbacks],
  );

  const { submitFullfillment, isLoading: isSubmitLoading } = useFullfillmentSubmit({
    merchantId,
    originalFulfillmentState,
    callbacks,
    noShippingRequiredConfig: noShippingConfig,
  });

  const [fulfillmentState, setFulfillmentState] =
    useState<FulfillmentState>(originalFulfillmentState);
  const [callbackUrlError, setCallbackUrlError] = useState<string | null>(null);

  useEffect(() => {
    // Reset the state when the original state changes
    setFulfillmentState(originalFulfillmentState);
  }, [originalFulfillmentState]);

  const {
    isShippingEnabled,
    isOrderResolveEnabled,
    isOrderChangeEnabled,
    isOrderVerifyEnabled,
    callbackUrl,
  } = fulfillmentState;
  const hasModifyAccess = canUpdateMerchantSettings();
  const isAnyCallbackInherited = callbacks?.some((c) => c.isInherited);

  const isSaveBarOpen =
    hasModifyAccess &&
    Object.keys(originalFulfillmentState).some(
      (key) =>
        originalFulfillmentState[key as keyof FulfillmentState] !==
        fulfillmentState[key as keyof FulfillmentState],
    );

  const inheritableSaveBarLabels = useMemo(
    () => getFulfillmentInheritableSaveBarLabels(originalFulfillmentState, fulfillmentState),
    [fulfillmentState, originalFulfillmentState],
  );

  return (
    <PageWrapper
      showParentHierarchyBanner
      title="Fulfillment"
      description="In order to set your callback URL you first need to enable Shipping"
      isLoading={isLoadingConfigs || isLoadingCallbacks || merchantInfoLoading}
      key={`${configs?.[0]?.merchantId}-${callbacks?.[0]?.merchantId}`}
    >
      <Stack spacing={4}>
        <ConfigSwitchSection
          title="Shipping"
          description="When you turn on shipping, Skipify will display your shipping options to your customers during checkout."
          isChecked={isShippingEnabled}
          onToggle={() =>
            setFulfillmentState((prevState) => ({
              ...prevState,
              isShippingEnabled: !prevState.isShippingEnabled,
            }))
          }
          isLoading={isLoadingConfigs}
          disabled={!hasModifyAccess}
        />
        <Stack sx={{ maxWidth: '448px' }} spacing={2}>
          <ConfigSwitchSection
            title="Callback URL"
            description={
              <>
                You can designate a URL below where we can perform a callback to get the correct
                and/or tax details based on your customer’s address. This URL can be enabled for the
                following three events available via the SDK:
                <Box component="ul" sx={{ paddingInline: 3, marginBlock: 1 }}>
                  <li>ORDER_RESOLVE - defines amount per line items or cart</li>
                  <li>ORDER_CHANGE - defines tax, shipping address, discounts</li>
                  <li>ORDER_VERIFY - validation before payment processing</li>
                </Box>
                <b> Please make sure to use a valid protocol (http:// or https://)</b>
              </>
            }
            isChecked={isOrderResolveEnabled}
            onToggle={(newIsOrderResolveEnabled) => {
              setFulfillmentState((prevState) => ({
                ...prevState,
                isOrderResolveEnabled: newIsOrderResolveEnabled,
                isOrderChangeEnabled: newIsOrderResolveEnabled && prevState.isOrderChangeEnabled,
                isOrderVerifyEnabled: newIsOrderResolveEnabled && prevState.isOrderVerifyEnabled,
                callbackUrl: newIsOrderResolveEnabled ? prevState.callbackUrl : '',
              }));
              if (!newIsOrderResolveEnabled) {
                setCallbackUrlError(null);
              }
            }}
            renderSwitchLabel={() => (
              <Typography component="span">
                {`Callback URL is currently `}
                <Typography sx={{ fontWeight: 'bold' }} component="span">
                  {`${isOrderResolveEnabled ? 'enabled ' : 'disabled '}`}
                </Typography>
                (<code>ORDER_RESOLVE</code> type)
              </Typography>
            )}
            isLoading={isLoadingCallbacks}
            disabled={!hasModifyAccess || isAnyCallbackInherited}
          />
          <Collapse in={isOrderResolveEnabled}>
            <Stack spacing={1}>
              <TextField
                required
                label="Callback URL"
                placeholder="Please enter URL"
                disabled={!isOrderResolveEnabled || !hasModifyAccess || isAnyCallbackInherited}
                value={callbackUrl}
                onChange={(e) =>
                  setFulfillmentState((prevState) => ({
                    ...prevState,
                    callbackUrl: e.target.value,
                  }))
                }
                helperText={callbackUrlError}
                error={!!callbackUrlError}
              />
              <Stack spacing={1}>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Typography
                    sx={{
                      color: isOrderResolveEnabled ? 'grey.900' : 'grey.500',
                      fontSize: '14px',
                    }}
                  >
                    Enable callbacks for the <code>ORDER_CHANGE</code> type
                  </Typography>
                  <Switch
                    isChecked={isOrderChangeEnabled}
                    onToggle={() =>
                      setFulfillmentState((prevState) => ({
                        ...prevState,
                        isOrderChangeEnabled: !prevState.isOrderChangeEnabled,
                      }))
                    }
                    isDisabled={
                      !isOrderResolveEnabled || !hasModifyAccess || isAnyCallbackInherited
                    }
                    aria-label="Order Change Switch"
                  />
                </Box>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Typography
                    sx={{
                      color: isOrderResolveEnabled ? 'grey.900' : 'grey.500',
                      fontSize: '14px',
                    }}
                  >
                    Enable callbacks for the <code>ORDER_VERIFY</code> type
                  </Typography>
                  <Switch
                    isChecked={isOrderVerifyEnabled}
                    onToggle={() =>
                      setFulfillmentState((prevState) => ({
                        ...prevState,
                        isOrderVerifyEnabled: !prevState.isOrderVerifyEnabled,
                      }))
                    }
                    isDisabled={
                      !isOrderResolveEnabled || !hasModifyAccess || isAnyCallbackInherited
                    }
                    aria-label="Order Verify Switch"
                  />
                </Box>
              </Stack>
            </Stack>
          </Collapse>
        </Stack>
      </Stack>
      <ParentAwareSaveBar
        show={isSaveBarOpen}
        onSave={() => {
          if (isOrderResolveEnabled) {
            const callbackError = validateCallbackUrl(callbackUrl);
            setCallbackUrlError(callbackError.length > 0 ? callbackError : null);
            if (callbackError.length > 0) return;
          }
          submitFullfillment(fulfillmentState);
        }}
        onCancel={() => {
          setFulfillmentState({ ...originalFulfillmentState });
          setCallbackUrlError(null);
        }}
        disableSave={isOrderResolveEnabled && callbackUrl.length === 0}
        saveLoading={isSubmitLoading}
        text={
          isSubmitLoading
            ? 'Saving edits to this page...'
            : 'You are currently configuring this page...'
        }
        inheritableSettingLabels={isSaveBarOpen ? inheritableSaveBarLabels : []}
      />
    </PageWrapper>
  );
};

export default FulfillmentComponent;
